import React, { useEffect, useState } from "react";
import Input from "../../../../components/new/inputs/input";
import { useToast } from "../../../../hooks/ToastContext";
import Label from "../../../../components/new/inputs/Label";
import LoaderButton from "../../../../components/new/inputs/LoaderButton";
import { Dialog } from "primereact/dialog";
import TicketView from "../Print/TicketView";
import { quickTicketView } from "../../../../services/counter/quickTicketView.service";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { getErrorMessage } from "../../helpers/booking-helper";

dayjs.extend(customParseFormat);

export default function QuickTicketViewModal({
    visible,
    setVisible,
    companyConfig = [],
    counterInfo = {},
}) {
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);

    const [ticktNumber, setTicketNumber] = useState("");
    const [ticketData, setTicketData] = useState(null);
    const [layout, setLayout] = useState(null);

    let setCompanyInfo = async () => {
        try {
            const layoutId = companyConfig.find(
                (item) => item.key === "physical_ticket_print_layout"
            );
            setLayout(layoutId?.value ?? null);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (companyConfig.length) {
            setCompanyInfo();
        }
    }, [companyConfig]);

    const getCounterInfo = () => {
        const counter = {
            title_en: "",
            address: "",
        };
        if (counterInfo?.id) {
            counter.title_en = counterInfo.title.en;
            counter.address = counterInfo.address;
        }

        return counter;
    };

    const search = async () => {
        setLoading(true);
        if (ticktNumber !== "") {
            try {
                let response = await quickTicketView({
                    ticket_number: ticktNumber,
                });

                if (response.data?.data?.length) {
                    const result = response.data.data[0];
                    const goodsFare = Number(
                        result.goods_data?.final_net_pay || 0
                    );
                    const grandTotal =
                        Number(
                            result.price_details?.total_discounted_seat_price ||
                                0
                        ) + goodsFare;

                    const seatFares = result?.price_details?.seat_fares?.map(
                        ({ fare }) => [fare, fare]
                    );

                    const discounts =
                        result?.price_details?.seat_fares?.reduce(
                            (total, { discount }) => total + Number(discount),
                            0
                        ) || 0;

                    const formatData = {
                        allowedToPrint: result?.allowed_to_print,
                        busTitle: result?.company_title,
                        busWebsite: "",
                        ticketNumber: result?.ticket_number,
                        tripNumber: result?.trip_number,
                        passengerName: result?.passenger?.name,
                        age: result?.passenger?.age ?? "",
                        address: result?.passenger?.address ?? "",
                        msisdn: result?.passenger?.mobile,
                        gender:
                            result?.passenger?.gender?.charAt(0).toUpperCase() +
                            result?.passenger?.gender?.slice(1),
                        journeyDate: dayjs(result?.journey_date).format(
                            "DD/MM/YYYY"
                        ),
                        issueDate: dayjs(result?.issue_date).format(
                            "DD/MM/YYYY"
                        ),
                        issueTime: dayjs(result?.issue_date, "HH:mm:ss").format(
                            "h:mm A"
                        ),
                        from:
                            result?.boarding_counter?.title?.en ||
                            result?.boarding_counter?.title,
                        to:
                            result?.dropping_counter?.title?.en ||
                            result?.dropping_counter?.title,
                        from_district: result?.boarding_counter?.district,
                        to_district: result?.dropping_counter?.district,
                        reportingTime: dayjs(result?.departure_time, "HH:mm:ss")
                            .subtract(
                                process.env
                                    .MIX_COUNTER_REPORTING_TIME_DIFFERENCE_FROM_DEPARTING_TIME ??
                                    30,
                                "minute"
                            )
                            .format("hh:mm A"),
                        departureTime: dayjs(
                            result?.departure_time,
                            "HH:mm:ss"
                        ).format("h:mm A"),
                        totalFare:
                            result?.price_details?.total_discounted_seat_price,
                        seatFare: Array.from(
                            new Map(seatFares || []).values()
                        ).join("/"), // 1000/2000
                        seats: result.seats.join(", "),
                        counter: getCounterInfo(),
                        user: result.issued_by?.name || "",
                        issueDateTime: result?.issue_date
                            ? dayjs(result?.issue_date).format(
                                  "DD/MM/YYYY HH:mm:ss"
                              )
                            : "",
                        issuedBy: result?.issued_by?.name,
                        issuedCounter: result?.issue_counter?.title?.en,
                        status: result?.status,
                        pnr: result?.pnr,
                        discounts,
                        goodsFare,
                        grandTotal,
                    };
                    setTicketData(formatData);
                    setVisible(true);
                } else {
                    toast.error({ detail: "Sorry, No ticket found." });
                }
            } catch (e) {
                toast.error({ detail: getErrorMessage(e) });
                console.log(e);
            }
        } else {
            toast.error({ detail: "Please insert ticket number" });
        }
        setLoading(false);
    };

    const closeDialog = () => {
        setVisible(false);
        setTicketData(null);
        setTicketNumber("");
    };

    return (
        <div className="card flex justify-content-center">
            <Dialog
                header="Quick Ticket View"
                draggable={false}
                visible={visible}
                onHide={closeDialog}
                style={{
                    width: "70vw",
                    height: "80vh",
                    minWidth: "900px",
                }}
            >
                <div className="mx-auto lg:w-1/2 md:w-full">
                    <div className="flex gap-4 items-start">
                        <div className="flex-1 flex flex-col gap-1">
                            <Label title="Quick ticket view" />
                            <Input
                                type="text"
                                className="w-full"
                                id="quick_ticket_view"
                                name="quick_ticket_view"
                                onChange={(e) =>
                                    setTicketNumber(e.target.value)
                                }
                                value={ticktNumber}
                                required={true}
                            />
                        </div>
                        <LoaderButton
                            type="button"
                            title="Search"
                            onClick={search}
                            loading={loading}
                            className="mt-6"
                        />
                    </div>
                </div>

                {ticketData ? (
                    <TicketView
                        data={ticketData}
                        isPassengerPartMiddle={layout === "counter_part_middle"}
                        closeDialog={closeDialog}
                    />
                ) : null}
            </Dialog>
        </div>
    );
}
